import React from 'react';
import { Skeleton } from '@mui/material';
import { Timeline } from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-lg rounded-xl border border-gray-100 p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

const ClaimsTrendSection = ({ data = [], loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <Skeleton variant="text" width={200} height={28} />
        <Skeleton variant="rounded" height={280} sx={{ mt: 2 }} />
      </div>
    );
  }

  const totalFiled = data.reduce((s, d) => s + d.filed, 0);
  const totalApproved = data.reduce((s, d) => s + d.approved, 0);
  const totalRejected = data.reduce((s, d) => s + d.rejected, 0);

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-center gap-2 mb-4">
        <Timeline sx={{ fontSize: 18, color: '#6366f1' }} />
        <h3 className="text-sm font-semibold text-gray-700">Claims Trend (Monthly)</h3>
        <div className="ml-auto flex items-center gap-4 text-xs text-gray-500">
          <span>
            Filed: <strong className="text-gray-700">{totalFiled}</strong>
          </span>
          <span>
            Approved: <strong className="text-emerald-600">{totalApproved}</strong>
          </span>
          <span>
            Rejected: <strong className="text-red-600">{totalRejected}</strong>
          </span>
        </div>
      </div>

      {data.every((d) => d.filed === 0) ? (
        <div className="flex items-center justify-center py-16 text-sm text-gray-400">
          No claims data for the last 12 months
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
            />
            <Bar
              dataKey="filed"
              name="Filed"
              fill="#6366f1"
              radius={[4, 4, 0, 0]}
              barSize={18}
            />
            <Bar
              dataKey="approved"
              name="Approved"
              fill="#10b981"
              radius={[4, 4, 0, 0]}
              barSize={18}
            />
            <Bar
              dataKey="rejected"
              name="Rejected"
              fill="#ef4444"
              radius={[4, 4, 0, 0]}
              barSize={18}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default ClaimsTrendSection;
